/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 *
 * This  software  has  been  provided pursuant  to  a  License
 * Agreement  containing  restrictions on  its  use.   The  software
 * contains  valuable  trade secrets and proprietary information  of
 * Pegasystems Inc and is protected by  federal   copyright law.  It
 * may  not be copied,  modified,  translated or distributed in  any
 * form or medium,  disclosed to third parties or used in any manner
 * not provided for in  said  License Agreement except with  written
 * authorization from Pegasystems Inc.
 */

import PegaConstants from '../constants';
import Utils from '../utils/utils';
import DataStore from '../datapage/index';
import type { Filter, Paging, Query } from '../datapage/types';
import { getDataPageMetadata } from '../rules/rules-store';
import type { DataObjectMeta } from '../analytics/types/data-model';
import { doesContextExist } from '../store/state/state-manager';

import type { DataAsyncResponse } from './types';
import DataApiUtils from './data-api-utils';
import BufferedDataView from './buffered-data-view';
import type {
  DataViewCall,
  DataViewCallCombiner,
  DataViewCallComparator,
  DataViewCallSeparator
} from './buffer-utils-types';
import addDefaultBufferFunctions from './buffer-utils';

interface Column {
  value: string;
  useForSearch?: boolean;
}

export interface Config {
  deferDatasource?: boolean;
  dataSourceMetaData?: { structure?: string; isQueryable?: boolean } | DataObjectMeta;
  associationFilter?: string[] | null;
  propertyFilter?: string[];
  matchPosition: string | undefined;
  ignoreCase: boolean | undefined;
  maxResultsDisplay?: string;
  enablePagination?: boolean;
  dataSource: string;
  parameters: { [param: string]: string };
  columns: Column[];
  cacheLifeSpan?: string;
  listType?: string;
  pageSize?: number;
  invalidateCache?: boolean;
}
/** when a large metadata content is returned the session storage cannot store it, ignoring the error as short time fix */
const storeMetadataInSession = (cacheKey: string, metadataString: string) => {
  try {
    window.sessionStorage.setItem(cacheKey, metadataString);
  } catch (err) {
    // Ignoring the error for now.
  }
};

class DataApi {
  isInitialised: boolean;
  isQueryable?: boolean;
  nonQueryableData: object[];
  fetchedNQData: boolean;
  previousSearchText: null | string;
  totalRecordCount: number;
  fetchedRecordCount: number;
  pageNumber: number;
  parameters?: { [param: string]: string };
  enablePagination?: boolean;
  columns?: Column[];
  pageSize?: number;
  maxResultsDisplay?: string;
  matchPosition?: string;
  ignoreCase?: boolean;
  cache: object;
  dataSourceMetaData?: { structure?: string; isQueryable?: boolean } | DataObjectMeta;
  isNQDataFetchInProgress: boolean;
  fetchNQDataPromise: Promise<{ data: object[] }> | null;
  dataSource: string;
  context?: string;
  associationFilter?: string[] | null;
  propertyFilter?: string[];
  listType?: string;
  bufferInitialized: boolean;
  constructor(config?: Config, context?: string) {
    this.isInitialised = false;
    this.isQueryable = true;
    this.dataSourceMetaData = {};
    this.nonQueryableData = [];
    this.fetchedNQData = false;
    this.previousSearchText = null;
    this.totalRecordCount = 0;
    this.fetchedRecordCount = 0;
    this.cache = {};
    this.pageNumber = 0;
    this.isNQDataFetchInProgress = false;
    this.fetchNQDataPromise = null;
    this.dataSource = '';
    this.bufferInitialized = false;
    if (config) {
      this.listType = config.listType;
      this.dataSource = config.dataSource;
      if (config.enablePagination) {
        this.enablePagination = config.enablePagination;
        this.pageSize = config.pageSize;
      } else {
        this.maxResultsDisplay = config.maxResultsDisplay;
      }
      this.parameters = config.parameters;
      this.columns = config.columns;
      this.matchPosition = config.matchPosition;
      this.ignoreCase = config.ignoreCase !== false;
      this.dataSourceMetaData = config.dataSourceMetaData;
      this.context = context;
    }
  }

  private static readonly searchResultsCache: {
    [context: string]: { [uniqueDataApiKey: string]: DataApi };
  } = {};

  static init(config: Config, context?: string) {
    return new Promise<DataApi>((resolve, reject) => {
      const columnsList = config.columns.map((column: Column) => column.value);
      const paramList = Object.entries(config.parameters).map(
        ([paramName, paramValue]) => `${paramName}+${paramValue}`
      );
      const uniqueList = [...columnsList, ...paramList, config.matchPosition, config.maxResultsDisplay].join(
        PegaConstants.HASH_DELIMITER
      );
      const hashDigest = Utils.getHashedString(uniqueList);
      const uniqueDataApiKey = `${config.dataSource}_${hashDigest}`;
      const cacheKey = DataApiUtils.getAppCacheKey(config.dataSource);
      if (window.sessionStorage.getItem(cacheKey)) {
        const metaDataCache = JSON.parse(`${window.sessionStorage.getItem(cacheKey)}`);
        if (context && DataApi.searchResultsCache?.[context]?.[uniqueDataApiKey]) {
          resolve(DataApi.searchResultsCache[context][uniqueDataApiKey]);
        } else if (config.cacheLifeSpan !== 'form' && DataApi.searchResultsCache?.persisted?.[uniqueDataApiKey]) {
          resolve(DataApi.searchResultsCache.persisted[uniqueDataApiKey]);
        } else {
          const metaDataObj = new DataApi(config, context);
          if (JSON.stringify(metaDataCache.associationFilter) === JSON.stringify(config.associationFilter)) {
            metaDataObj.dataSourceMetaData = metaDataCache.dataSourceMetaData;
            metaDataObj.isQueryable = metaDataCache.isQueryable;
            metaDataObj.isInitialised = metaDataCache.isInitialised;
            metaDataObj.associationFilter = metaDataCache.associationFilter;
            DataApi.initializeCache(context, config, uniqueDataApiKey, metaDataObj);
            resolve(metaDataObj);
          } else {
            metaDataObj.associationFilter = config.associationFilter;
            metaDataObj.propertyFilter = config.propertyFilter;
            metaDataObj
              .fetchMetaData(context)
              .then((response: { data: { isQueryable: boolean } }) => {
                storeMetadataInSession(
                  cacheKey,
                  JSON.stringify({
                    isQueryable: response.data.isQueryable,
                    dataSourceMetaData: response.data,
                    isInitialised: true,
                    associationFilter: config.associationFilter
                  })
                );
                metaDataObj.isQueryable = response.data.isQueryable;
                metaDataObj.dataSourceMetaData = response.data;
                metaDataObj.isInitialised = true;
                DataApi.initializeCache(context, config, uniqueDataApiKey, metaDataObj);
                resolve(metaDataObj);
              })
              .catch((err: Error) => {
                metaDataObj.isInitialised = false;
                reject(err);
              });
          }
        }
      } else if (config.deferDatasource && getDataPageMetadata(config.dataSource)) {
        const dataApiObj = new DataApi(config, context);
        const datapageMetadata = getDataPageMetadata(config.dataSource) as DataApi['dataSourceMetaData'] | null;
        dataApiObj.isQueryable = !!datapageMetadata?.isQueryable;
        dataApiObj.isInitialised = true;
        dataApiObj.dataSourceMetaData = datapageMetadata as DataApi['dataSourceMetaData'];
        storeMetadataInSession(
          cacheKey,
          JSON.stringify({
            isQueryable: datapageMetadata?.isQueryable,
            dataSourceMetaData: datapageMetadata,
            isInitialised: true
          })
        );
        dataApiObj.context = context;
        DataApi.initializeCache(context, config, uniqueDataApiKey, dataApiObj);
        resolve(dataApiObj);
      } else {
        const dataApiObj = new DataApi(config, context);
        dataApiObj.associationFilter = config.associationFilter;
        dataApiObj.propertyFilter = config.propertyFilter;
        dataApiObj
          .fetchMetaData(context)
          .then((response: { data: { isQueryable: boolean } }) => {
            storeMetadataInSession(
              cacheKey,
              JSON.stringify({
                isQueryable: response.data.isQueryable,
                dataSourceMetaData: response.data,
                isInitialised: true,
                associationFilter: config.associationFilter
              })
            );
            dataApiObj.isQueryable = response.data.isQueryable;
            dataApiObj.dataSourceMetaData = response.data;
            dataApiObj.isInitialised = true;
            DataApi.initializeCache(context, config, uniqueDataApiKey, dataApiObj);
            resolve(dataApiObj);
          })
          .catch((err: Error) => {
            dataApiObj.isInitialised = false;
            reject(err);
          });
      }
    });
  }

  static readonly bufferedDataView = new BufferedDataView<DataViewCall>();

  registerForBufferedCall(bufferedCallParams: {
    combiner?: DataViewCallCombiner;
    comparator?: DataViewCallComparator;
    separator?: DataViewCallSeparator;
    waitTime?: number;
    maxSize?: number;
  }) {
    if (this.isQueryable) {
      this.initializeBufferCallsForDataPage(addDefaultBufferFunctions(bufferedCallParams));
      this.bufferInitialized = true;
    }
    return this;
  }

  static clearContextedCache(context: string) {
    delete DataApi.searchResultsCache[context];
  }

  static initializeCache(context: string | undefined, config: Config, uniqueDataApiKey: string, dataApiObj: DataApi) {
    if (context && config.cacheLifeSpan?.toLowerCase() === 'form') {
      if (DataApi.searchResultsCache[context] === undefined) {
        DataApi.searchResultsCache[context] = {};
      }
      DataApi.searchResultsCache[context][uniqueDataApiKey] = dataApiObj;
    } else {
      if (DataApi.searchResultsCache.persisted === undefined) {
        DataApi.searchResultsCache.persisted = {};
      }
      DataApi.searchResultsCache.persisted[uniqueDataApiKey] = dataApiObj;
    }
  }

  fetchMetaData(context: string | undefined) {
    return DataApiUtils.getDataViewMetadata(this.dataSource, context, this.associationFilter, this.propertyFilter);
  }

  initializeBufferCallsForDataPage(bufferedCallParams: {
    combiner: DataViewCallCombiner;
    comparator: DataViewCallComparator;
    separator: DataViewCallSeparator;
    waitTime?: number;
    maxSize?: number;
  }) {
    const finalDataViewCall = (queryableDPSchema: DataViewCall) => {
      return DataStore.getDataAsync(
        this.dataSource,
        this.context,
        queryableDPSchema.dataViewParameters,
        queryableDPSchema.paging,
        queryableDPSchema.query
      );
    };
    DataApi.bufferedDataView.addBufferedDataQueueForDataPage(
      this.dataSource,
      finalDataViewCall,
      bufferedCallParams.combiner,
      bufferedCallParams.separator,
      bufferedCallParams.comparator,
      bufferedCallParams.waitTime,
      bufferedCallParams.maxSize
    );
  }

  buildSchemaForQueryableBufferDP(searchText: string): DataViewCall {
    return {
      ...this.buildSchemaForQueryableDP(searchText),
      paging: undefined,
      filterDataOnClient: this.filterDataOnClient.bind(this)
    };
  }

  getData(searchText: string, overriddenConfig?: Partial<Config>) {
    if (this.isQueryable) {
      if (this.bufferInitialized) {
        try {
          const queryableBufferDPSchema = this.buildSchemaForQueryableBufferDP(searchText);
          return DataApi.bufferedDataView
            .getBufferedDataQueueForDataPage(this.dataSource)
            .queueDataViewCall(queryableBufferDPSchema, searchText);
        } catch {
          const queryableDPSchemaCall = this.buildSchemaForQueryableDP(searchText, overriddenConfig);
          return DataStore.getDataAsync(
            this.dataSource,
            this.context,
            queryableDPSchemaCall.dataViewParameters,
            queryableDPSchemaCall.paging,
            queryableDPSchemaCall.query
          );
        }
      }
      const queryableDPSchema = this.buildSchemaForQueryableDP(searchText, overriddenConfig);
      return DataStore.getDataAsync(
        this.dataSource,
        this.context,
        queryableDPSchema.dataViewParameters,
        queryableDPSchema.paging,
        queryableDPSchema.query
      );
    }
    return this.fetchNonQueryableResults(searchText, overriddenConfig);
  }

  memoizedGetData(searchText: string, overriddenConfig?: Partial<Config>) {
    const searchTextInLowerCase = searchText?.toLowerCase();
    const isResultCached = this.enablePagination
      ? searchTextInLowerCase in this.cache &&
        this.pageNumber in this.cache[searchTextInLowerCase as keyof DataApi['cache']]
      : searchTextInLowerCase in this.cache;
    if (isResultCached) {
      if (this.enablePagination) {
        return this.cache[searchTextInLowerCase as keyof DataApi['cache']][this.pageNumber];
      }
      return this.cache[searchTextInLowerCase as keyof DataApi['cache']];
    }
    const result = this.getData(searchText, overriddenConfig);
    if (this.enablePagination) {
      this.cache[searchTextInLowerCase as keyof DataApi['cache']] =
        this.cache[searchTextInLowerCase as keyof DataApi['cache']] || {};
      (this.cache[searchTextInLowerCase as keyof DataApi['cache']][this.pageNumber] as
        | Promise<unknown>
        | null
        | undefined) = result;
    } else {
      (this.cache[searchTextInLowerCase as keyof DataApi['cache']] as Promise<unknown> | null | undefined) = result;
    }
    return result;
  }
  /* eslint prefer-promise-reject-errors: ["error", {"allowEmptyReject": true}] */

  fetchData(searchText: string, isPaginated = false, overriddenConfig?: Partial<Config>) {
    if (!this.isValidDatasource()) {
      return Promise.reject(new Error());
    }
    if (this.context && !doesContextExist(this.context)) {
      // If context does not exist then return empty data
      return Promise.resolve({ data: [] });
    }
    if (this.isInitialised) {
      this.pageNumber = isPaginated ? this.pageNumber : 1;
      return this.memoizedGetData(searchText, overriddenConfig);
    }
    return Promise.reject(new Error());
  }

  fetchPaginatedData(searchText: string) {
    this.pageNumber += 1;
    return this.fetchData(searchText, true);
  }

  fetchNonQueryableResults(searchText: string, overriddenConfig?: Partial<Config>): Promise<DataAsyncResponse | null> {
    if (searchText === undefined) {
      // if searchText is undefined, then returning empty data
      return Promise.resolve({ data: [] });
    }
    if (this.fetchedNQData) {
      return Promise.resolve({
        data: this.filterDataOnClient(
          searchText,
          this.nonQueryableData as { [key: string]: string }[],
          overriddenConfig
        )
      });
    }
    if (this.isNQDataFetchInProgress && this.fetchNQDataPromise) {
      return this.fetchNQDataPromise
        .then(() => {
          return {
            data: this.filterDataOnClient(
              searchText,
              this.nonQueryableData as { [key: string]: string }[],
              overriddenConfig
            )
          };
        })
        .catch((err: Error) => Promise.reject(err));
    }
    if (
      this.dataSourceMetaData &&
      'structure' in this.dataSourceMetaData &&
      this.dataSourceMetaData.structure?.toLowerCase() === 'list'
    ) {
      const nonQueryableDPSchema = this.buildSchemaForNonQueryableDP();
      this.fetchNQDataPromise = DataStore.getDataAsync(
        this.dataSource,
        this.context,
        nonQueryableDPSchema.dataViewParameters,
        undefined,
        undefined,
        overriddenConfig
      );
      this.isNQDataFetchInProgress = true;
      return new Promise((resolve, reject) => {
        this.fetchNQDataPromise
          ?.then((response: { data: object[] }) => {
            this.nonQueryableData = [...response.data];
            this.fetchedNQData = true;
            return resolve({
              data: this.filterDataOnClient(
                searchText,
                this.nonQueryableData as { [key: string]: string }[],
                overriddenConfig
              )
            });
          })
          .catch((err: Error) => reject(err))
          .finally(() => {
            this.isNQDataFetchInProgress = false;
            this.fetchNQDataPromise = null;
          });
      });
    }
    return Promise.resolve(null);
  }

  filterDataOnClient(
    searchText: string,
    clientSideData: { [key: string]: string }[],
    overriddenConfig?: Partial<Config>
  ) {
    const searchColumns = (overriddenConfig?.columns ?? this.columns)
      ?.filter((column) => column.useForSearch)
      .map((column) => column.value);
    return clientSideData.reduce((filteredResults: { [key: string]: string }[], result: { [key: string]: string }) => {
      Object.keys(result).every((key) => {
        if (
          searchColumns?.includes(key) &&
          result[key] &&
          this.positionMatches(`${result[key]}`, searchText, overriddenConfig)
        ) {
          filteredResults.push({ ...result });
          return false;
        }
        return true;
      });
      return filteredResults;
    }, []);
  }

  positionMatches(text: string, searchText: string, overriddenConfig?: Partial<Config>) {
    const ignoreCase = overriddenConfig?.ignoreCase ?? this.ignoreCase;
    const textToMatch = ignoreCase ? text.toLowerCase() : text;
    const searchTextToMatch = ignoreCase ? searchText.toLowerCase() : searchText;
    switch (overriddenConfig?.matchPosition ?? this.matchPosition) {
      case 'start':
        return textToMatch.startsWith(searchTextToMatch);
      case 'contains':
        return textToMatch.includes(searchTextToMatch);
      case 'equals':
        return textToMatch === searchTextToMatch;
      default:
        return false;
    }
  }

  // Build Select
  buildSelect(overriddenConfig?: Partial<Config>) {
    const selectArray: { field: string }[] = [];
    const colsArr: string[] = [];
    (overriddenConfig?.columns ?? this.columns)?.forEach((column) => {
      if (!colsArr.includes(column.value)) {
        colsArr.push(column.value);
        const selectObj = { field: column.value };
        selectArray.push(selectObj);
      }
    });
    return selectArray;
  }

  // Build filters
  buildFilters(searchText: string, overriddenConfig?: Partial<Config>): Filter {
    const matchPosition = overriddenConfig?.matchPosition ?? this.matchPosition;
    const ignoreCase = overriddenConfig?.ignoreCase ?? this.ignoreCase;
    const filterConditions: {
      [k: string]: {
        lhs: { field: string };
        rhs: {
          value?: string;
          values?: string[];
        };
        comparator: string;
        ignoreCase?: string;
      };
    } = {};
    let index = 0;
    (overriddenConfig?.columns ?? this.columns)?.forEach((column) => {
      if (column.useForSearch) {
        const filterCondition = `F${index}`;
        index += 1;
        const lhs = {
          field: column.value
        };
        const rhs = {
          value: searchText
        };
        let comparator = 'CONTAINS';

        if (matchPosition === 'start') {
          comparator = 'STARTS_WITH';
        } else if (matchPosition === 'equals') {
          comparator = 'EQ';
        }
        const filterConditionObj = {
          ...((`${parseFloat(searchText)}` !== searchText) === true && {
            ignoreCase: ignoreCase ? 'true' : 'false'
          }),
          lhs,
          rhs,
          comparator
        };
        filterConditions[filterCondition] = filterConditionObj;
      }
    });
    const logicString = Object.keys(filterConditions).join(' OR ');
    return {
      filterConditions,
      logic: logicString
    };
  }

  buildQuery(searchText: string, overriddenConfig?: Partial<Config>): Query {
    const select = this.buildSelect(overriddenConfig);
    if (!searchText) {
      return {
        select,
        ...(DataApiUtils.isDistinctResultsOnly(select) && { distinctResultsOnly: 'true' })
      };
    }
    return {
      select,
      filter: this.buildFilters(searchText, overriddenConfig),
      ...(DataApiUtils.isDistinctResultsOnly(select) && { distinctResultsOnly: 'true' })
    };
  }

  buildPageOptions(): Paging {
    return this.enablePagination
      ? {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }
      : {
          maxResultsToFetch: this.maxResultsDisplay
        };
  }

  buildSchemaForQueryableDP(searchText: string, overriddenConfig?: Partial<Config>) {
    return {
      dataViewParameters: this.parameters,
      query: this.buildQuery(searchText, overriddenConfig),
      paging: this.buildPageOptions()
    };
  }

  buildSchemaForNonQueryableDP() {
    return {
      dataViewParameters: this.parameters
    };
  }

  isValidDatasource() {
    return !!this.dataSource;
  }
}

export default DataApi;
